import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterItem,
  CharacterSpell,
  Item,
  PseudoItem,
  Spell,
  StoredSpell,
  SummonOption2,
} from "@dndModels";
import { ModelBase } from "@models";
import { SpellLevel } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import FilterBox from "@dndComponents/filtering/FilterBox";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import StoredSpellControl from "@dndComponents/character/inventory/StoredSpellControl";

interface Props {
  char: Character;
  char_item: CharacterItem;
  item: Item;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function StoredSpellsControls({
  char,
  char_item,
  item
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [view, setView] = useState("Current");
  const [spells, setSpells] = useState<Spell[]>([]);
  const [filtered, setFiltered] = useState<Spell[]>([]);
  const [max_count, setMaxCount] = useState(0);
  const [spell_attack, setSpellAttack] = useState(0);
  const [spell_dc, setSpellDC] = useState(0);
  const [spell_mod, setSpellMod] = useState(0);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      const new_spells = char_util.getSpellsForLimit({ char: char, limit: item.spell_limit });
      setSpells(new_spells);
      setFiltered(new_spells);
      const new_max_count = char_util.value(item.count_rules.max_count, { obj: char, item_id: char_item.true_id });
      setMaxCount(new_max_count);
      let max_dc = 0;
      let max_attack = 0;
      let max_mod = 0;
      for (const char_class of char.spirit.classes) {
        max_dc = Math.max(max_dc, char_class.spell_dc);
        max_attack = Math.max(max_attack, char_class.spell_attack);
        max_mod = Math.max(max_mod, char_class.spellcasting_modifier);
      }
      setSpellDC(max_dc);
      setSpellAttack(max_attack);
      setSpellMod(max_mod);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateFilter = (changed: any) => {
    const use_filter = {...changed};
    let new_filtered = [...spells];
    if (use_filter.known !== undefined) {
      const known_spell_ids = char.mind.spellbook_spells.map(o => o.spell_id);
      known_spell_ids.push(...char.mind.spells.map(o => o.spell_id));
      if (use_filter.known) {
        new_filtered = new_filtered.filter(o => known_spell_ids.includes(o._id));
      } else {
        new_filtered = new_filtered.filter(o => !known_spell_ids.includes(o._id));
      }
      delete use_filter.known;
    }
    new_filtered = api.filter("spell", new_filtered, use_filter) as Spell[];
    setFiltered(new_filtered);
    setReloading(true);
  }

  const renderLevelButton = (obj: Spell, level: SpellLevel, new_stored_total: number) => {
    if (level >= obj.level && new_stored_total + level <= max_count) {
      return (
        <Grid
          key={level}
          size={{
            xs: 12
          }}>
          <ButtonBox  
            name={`Add at Level ${level}`}
            onClick={() => {
              const new_stored_spell = new StoredSpell();
              new_stored_spell.spell_id = obj._id; 
              new_stored_spell.level = level;
              new_stored_spell.spell_attack = spell_attack;
              new_stored_spell.spell_dc = spell_dc;
              new_stored_spell.spell_mod = spell_mod;
              char_item.stored_spells.push(new_stored_spell);
              char.subscription_holder.itemsChanged();
              char.subscription_holder.recalculate();
              char.changed();
              setPing(!ping);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const spell_ids = char_item.stored_spells.map(o => o.spell_id);
  // console.log(filtered);
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={["Current", "Add"]}
          selected={view}
          onSelect={setView}
        />
      </Grid>
      { view === "Current" ?
        <>
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>
            Stored Spells
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox 
              name="Remove All Cast Spells"
              disabled={char_item.stored_spells.filter(o => o.has_been_cast === 1).length === 0}
              onClick={() => {
                char_item.stored_spells = char_item.stored_spells.filter(o => o.has_been_cast === 0);
                char.changed();
                char.subscription_holder.itemsChanged();
                char.subscription_holder.recalculate();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox 
              name="Remove All"
              disabled={char_item.stored_spells.length === 0}
              onClick={() => {
                char_item.stored_spells = [];
                char.changed();
                char.subscription_holder.itemsChanged();
                char.subscription_holder.recalculate();
                setPing(!ping);
              }}
            />
          </Grid>
          { char_item.stored_spells.map(stored_spell => {
            const spell = spells.find(o => o._id === stored_spell.spell_id);
            if (spell) {
              return (
                <Grid
                  key={stored_spell.true_id}
                  className='padded'
                  size={{
                    xs: 12
                  }}>
                  <StoredSpellControl 
                    char={char} 
                    stored_spell={stored_spell} 
                    spell={spell} 
                    onDelete={() => {
                      char_item.stored_spells = char_item.stored_spells.filter(o => o.true_id !== stored_spell.true_id);
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      char.subscription_holder.recalculate();
                      setPing(!ping);
                    }}
                  />
                  {/* <Grid container spacing={0} direction="row"
                    className={cx(classes.root, "padded")}>
                    <Grid item xs={5} 
                      className="clickable" 
                      onClick={() => {
                        setExpanded(expanded === stored_spell.true_id ? "" : stored_spell.true_id);
                      }}>
                      <Grid container spacing={0} direction="row">
                        <Grid item xs={12}>
                          <ViewSpell
                            spell={spell}
                          />
                        </Grid>
                        { stored_spell.level !== spell.level &&
                          <Grid item xs={12} className="small_label gray">
                            Stored at Level {stored_spell.level}
                          </Grid>
                        }
                      </Grid>
                    </Grid>
                    <Grid item xs={5} 
                      className="clickable" 
                      onClick={() => {
                        setExpanded(expanded === stored_spell.true_id ? "" : stored_spell.true_id);
                      }}>
                      <Grid container spacing={0} direction="row">
                        <Grid item xs={4}>
                          Attack: { stored_spell.spell_attack }
                        </Grid>
                        <Grid item xs={4}>
                          DC: { stored_spell.spell_dc }
                        </Grid>
                        <Grid item xs={4}>
                          Mod: { stored_spell.spell_mod }
                        </Grid>
                        { stored_spell.has_been_cast === 1 &&
                          <Grid item xs={12}>Has Been Cast</Grid>
                        }
                      </Grid>
                    </Grid>
                    <Grid item xs={2}>
                      <DeleteButton
                        onClick={() => {
                          char_item.stored_spells = char_item.stored_spells.filter(o => o.true_id !== stored_spell.true_id);
                          char.changed();
                          char.subscription_holder.itemsChanged();
                          char_util.repopulateCharFeatureBases(char);
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    { expanded === stored_spell.true_id &&
                      <Grid item xs={12} className="bordered padded" style={{ backgroundColor: "black" }}>
                        <Grid container spacing={0} direction="row">
                          <Grid item xs={4}>
                            <StringBox
                              name="Spell Attack"
                              value={`${spell_attack}`}
                              type="number"
                              onBlur={(changed: string) => {
                                stored_spell.spell_attack = +changed;
                                setPing(!ping);
                                char.changed();
                                char.subscription_holder.itemsChanged();
                                char_util.repopulateCharFeatureBases(char);
                              }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <StringBox
                              name="Spell DC"
                              value={`${spell_dc}`}
                              type="number"
                              onBlur={(changed: string) => {
                                stored_spell.spell_dc = +changed;
                                setPing(!ping);
                                char.changed();
                                char.subscription_holder.itemsChanged();
                                char_util.repopulateCharFeatureBases(char);
                              }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <StringBox
                              name="Spell Modifier"
                              value={`${spell_mod}`}
                              type="number"
                              onBlur={(changed: string) => {
                                stored_spell.spell_mod = +changed;
                                setPing(!ping);
                                char.changed();
                                char.subscription_holder.itemsChanged();
                                char_util.repopulateCharFeatureBases(char);
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    }
                  </Grid> */}
                </Grid>
              );
            }
            return null;
          })}
        </>
      :
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <FilterBox
              data_type="spell"
              flat
              dont_use_api
              all_objects={spells}
              c_options={char.c_options}
              the_filter={{ search_string: [""] }}
              extra_fields={["known"]}
              quick_fields={["school","spell_level","spell_list","known"]}
              onChange={updateFilter}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { !reloading &&
              <CharacterListOptionsInput
                data_type="spell"
                character={char}
                use_objects={filtered}
                selected={spell_ids.filter(o => o !== "")} 
                sort={(obj1: ModelBase | CharacterItem | CharacterSpell | SummonOption2 | PseudoItem, obj2: ModelBase | CharacterItem | CharacterSpell | SummonOption2 | PseudoItem) => {
                  if (obj1 instanceof Spell && obj2 instanceof Spell) {
                    if (obj1.level === obj2.level) {
                      return obj1.name.localeCompare(obj2.name);
                    } else {
                      return obj1.level > obj2.level ? 1 : -1;
                    }
                  }
                  return 0;
                }}
                // selected_elsewhere={character.mind.spells.filter(o => !spell_ids.includes(o.spell_id)).map(o => o.spell_id)} 
                // onChange={setSpellIDs} 
                // getDisabledReasons={(obj: ModelBase | CharacterItem | CharacterSpell) => {
                //   if (obj instanceof Spell && !spell_ids.includes(obj._id)) {
                //     let new_stored_total = 0;
                //     if (item.count_rules.slot_based_count) {
                //       new_stored_total = char_item.stored_spells.map(o => +o.level).reduce((a, b) => a + b, 0);
                //       if (new_stored_total + obj.level > max_count) {
                //         return "Max Reached";
                //       }
                //     } else { 
                //       new_stored_total = char_item.stored_spells.length + spell_ids.length;
                //       if (new_stored_total >= max_count) {
                //         return "Max Reached";
                //       }
                //     }
                //   }
                //   return "";
                // }}
                extra_options={(obj: ModelBase | CharacterItem | CharacterSpell | SummonOption2 | PseudoItem) => {
                  // console.log(obj);
                  if (obj instanceof Spell) {
                    let new_stored_total = 0;
                    if (item.count_rules.slot_based_count) {
                      // const selected_spells = spells.filter(o => spell_ids.includes(o._id));
                      new_stored_total = char_item.stored_spells.map(o => +o.level).reduce((a, b) => a + b, 0);
                      
                      return (
                        <>
                          { renderLevelButton(obj, 1, new_stored_total) }
                          { renderLevelButton(obj, 2, new_stored_total) }
                          { renderLevelButton(obj, 3, new_stored_total) }
                          { renderLevelButton(obj, 4, new_stored_total) }
                          { renderLevelButton(obj, 5, new_stored_total) }
                          { renderLevelButton(obj, 6, new_stored_total) }
                          { renderLevelButton(obj, 7, new_stored_total) }
                          { renderLevelButton(obj, 8, new_stored_total) }
                          { renderLevelButton(obj, 9, new_stored_total) }
                        </>
                      );
                    } 
                  }
                  return <></>;
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Spell Attack"
              value={`${spell_attack}`}
              type="number"
              onBlur={(changed: string) => {
                setSpellAttack(+changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Spell DC"
              value={`${spell_dc}`}
              type="number"
              onBlur={(changed: string) => {
                setSpellDC(+changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Spell Modifier"
              value={`${spell_mod}`}
              type="number"
              onBlur={(changed: string) => {
                setSpellMod(+changed);
              }}
            />
          </Grid>
        </>
      }
    </Grid>
  );
}
